import { toast } from "react-toastify";
import useUpdateOrderItemStatus from "../../../Routes/Marketplace/libs/useUpdateOrderItemStatus";
import useMarkCodRefused from "../../../Routes/Marketplace/libs/useMarkCodRefused";
import StatusBadge from "../../components/StatusBadge";
import ConfirmModal from "../../components/ConfirmModal";
import { getMerchantItems } from "../utils/orderHelpers";
import OrderPaymentBadge from "../../../Routes/Marketplace/components/OrderPaymentBadge";
import { isPayOnDeliveryOrder, formatNgn } from "../../../Routes/Marketplace/libs/orderDisplayUtils";
import { useState } from "react";

const STATUS_OPTIONS = ["Pending", "Processing", "Shipped", "Delivered", "Canceled"];

const MerchantOrderDetail = ({ order, merchantId, returnRequest, onReturnDecision, onOrderUpdate }) => {
  const { mutate: updateItemStatus } = useUpdateOrderItemStatus();
  const { mutate: markCodRefused, isLoading: refusingCod } = useMarkCodRefused();
  const [refuseOpen, setRefuseOpen] = useState(false);

  if (!order) return null;

  const canRecordRefusal =
    isPayOnDeliveryOrder(order) &&
    order.paymentStatus === "pending" &&
    ["Processing", "Shipped"].includes(order.status);

  const handleItemStatusChange = (itemId, status) => {
    updateItemStatus(
      { orderId: order._id, itemId, status },
      {
        onSuccess: (data) => {
          toast.success("Item status updated");
          onOrderUpdate?.(data?.order);
        },
        onError: (err) => toast.error(err.response?.data?.message || "Update failed"),
      }
    );
  };

  const handleConfirmRefusal = () => {
    markCodRefused(order._id, {
      onSuccess: (data) => {
        toast.success(data.message || "Delivery refusal recorded");
        onOrderUpdate?.(data.order);
        setRefuseOpen(false);
      },
      onError: (err) =>
        toast.error(err.response?.data?.message || "Could not record refusal"),
    });
  };

  return (
    <>
      <p><strong>Overall status:</strong> <StatusBadge status={order.status} /></p>
      <p><strong>Payment:</strong> <OrderPaymentBadge order={order} showDue /></p>
      {isPayOnDeliveryOrder(order) && order.paymentStatus === "pending" && (
        <p className="order-payment-badge-due">
          Customer pays {formatNgn(order.totalAmount)} at delivery
        </p>
      )}
      <p><strong>Customer:</strong> {order.shippingAddress?.full_name}</p>
      <p><strong>Phone:</strong> {order.shippingAddress?.phone_number}</p>
      <p><strong>Email:</strong> {order.shippingAddress?.email || "—"}</p>
      <p><strong>Address:</strong> {order.shippingAddress?.address}, {order.shippingAddress?.city}, {order.shippingAddress?.state}</p>
      <p><strong>Placed:</strong> {new Date(order.createdAt).toLocaleString()}</p>

      {canRecordRefusal && (
        <div style={{ marginTop: 16 }}>
          <button type="button" className="dashboard-btn dashboard-btn--danger" onClick={() => setRefuseOpen(true)}>
            Customer refused delivery
          </button>
          <p style={{ fontSize: 13, color: "var(--dash-muted)", marginTop: 8 }}>
            Restores stock and suspends customer pay-on-delivery for 60 days.
          </p>
        </div>
      )}

      {returnRequest && (
        <div className="dashboard-return-panel" style={{ marginTop: 16 }}>
          <h4 style={{ marginTop: 0 }}>Return request</h4>
          <p className="dashboard-return-panel__reason">{returnRequest.reason}</p>
          <p className="dashboard-form-hint">
            Status: <strong>{returnRequest.status}</strong>
          </p>
          {returnRequest.status === "pending" && onReturnDecision && (
            <div className="dashboard-form-actions">
              <button
                type="button"
                className="dashboard-btn dashboard-btn--primary"
                onClick={() => onReturnDecision(returnRequest._id, "approved")}
              >
                Approve return
              </button>
              <button
                type="button"
                className="dashboard-btn dashboard-btn--danger"
                onClick={() => onReturnDecision(returnRequest._id, "rejected")}
              >
                Reject return
              </button>
            </div>
          )}
        </div>
      )}

      <h4 style={{ marginTop: 20 }}>Your items</h4>
      <ul style={{ listStyle: "none", padding: 0 }}>
        {getMerchantItems(order, merchantId).map((item) => (
          <li key={item._id} style={{ marginBottom: 12, padding: 12, background: "#f9fafb", borderRadius: 8 }}>
            <div style={{ marginBottom: 8 }}>
              {item.name} × {item.quantity}
              {item.size ? ` (${item.size})` : item.selectedSize ? ` (${item.selectedSize})` : ""}
            </div>
            <select
              value={item.status || "Pending"}
              onChange={(e) => handleItemStatusChange(item._id, e.target.value)}
            >
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </li>
        ))}
      </ul>

      <ConfirmModal
        open={refuseOpen}
        title="Record delivery refusal?"
        message="The order will be canceled, stock restored, and the customer won't be able to use pay-on-delivery for 60 days."
        confirmLabel="Record refusal"
        danger
        loading={refusingCod}
        onConfirm={handleConfirmRefusal}
        onCancel={() => setRefuseOpen(false)}
      />
    </>
  );
};

export default MerchantOrderDetail;
